import React, { useEffect, useState, useMemo } from 'react';

// Comps
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import ProductTile from '@src/stories/ecommerce/Product/ProductTile';
import ProductListPagination from '@src/stories/ecommerce/Product/ProductListPagination';

// Store
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Utils
import { Button } from '@src/stories/elements/Button/Button';
import shopifyHelpers from 'frontend/js/shopify';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

// Types
import { IGraphQlProduct } from '@src/types/product';

export const AccountWishlist = ({ settings, locales }) => {
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();
  const getShopifyCustomerId = (): string | number | null => {
    return (
      (window as any).getShopifyCustomerId?.() ||
      (window as any).ShopifyAnalytics?.meta?.page?.customerId ||
      (window as any).customer?.id ||
      null
    );
  };

  // Store
  const localLists = useLocalWishlistStore((state) => state.localLists);
  const resetLocalLists = useLocalWishlistStore((state) => state.resetLocalLists);
  const wishlist = useMemo(() => {
    return localLists?.find((list) => list.listType === 'products') || null;
  }, [localLists]);

  // States
  const [products, setProducts] = useState<IGraphQlProduct[] | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PRODUCTS_PER_PAGE = 10;
  const customerId = getShopifyCustomerId();
  const blockSettings = settings?.settings || {};

  const getResponsiveValue = (desktopValue, mobileValue, tabletValue, ultrawideValue) => {
    if (isMobile) return mobileValue ?? desktopValue ?? 0;
    if (isTablet) return tabletValue ?? desktopValue ?? 0;
    if (isUltrawide) return ultrawideValue ?? desktopValue ?? 0;
    return desktopValue ?? 0;
  };

  const containerStyles = blockSettings.override_global_spacing
    ? {
        paddingTop: `${getResponsiveValue(
          blockSettings.padding_top,
          blockSettings.padding_top_mobile,
          blockSettings.padding_top_tablet,
          blockSettings.padding_top_ultrawide
        )}px`,
        paddingBottom: `${getResponsiveValue(
          blockSettings.padding_bottom,
          blockSettings.padding_bottom_mobile,
          blockSettings.padding_bottom_tablet,
          blockSettings.padding_bottom_ultrawide
        )}px`,
        paddingLeft: `${getResponsiveValue(
          blockSettings.padding_left,
          blockSettings.padding_left_mobile,
          blockSettings.padding_left_tablet,
          blockSettings.padding_left_ultrawide
        )}px`,
        paddingRight: `${getResponsiveValue(
          blockSettings.padding_right,
          blockSettings.padding_right_mobile,
          blockSettings.padding_right_tablet,
          blockSettings.padding_right_ultrawide
        )}px`
      }
    : undefined;



  // Re-fetch products when there are updates in wishlist
  useEffect(() => {
    const fetchProducts = async () => {
      setProducts(null);
      setCurrentPage(1);
      setIsLoadingProducts(true);

      if (!wishlist || !wishlist.products || wishlist.products.length === 0) {
        setProducts([]);
        setIsLoadingProducts(false);
        return;
      }

      try {
        const graphQlProducts = await shopifyHelpers.getShopifyProductList(wishlist.products);
        setProducts(graphQlProducts);
      } catch (error) {
        console.error('Error fetching wishlist products:', error);
        setProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [JSON.stringify(wishlist?.products || []), customerId]);

  const isLoading = isLoadingProducts;

  // Pagination logic
  const totalPages = products ? Math.ceil(products.length / PRODUCTS_PER_PAGE) : 1;
  const paginatedProducts = products
    ? products.slice((currentPage - 1) * PRODUCTS_PER_PAGE, currentPage * PRODUCTS_PER_PAGE)
    : [];

  return (
    <AccountSectionLayout
      classes={`w-full hide-scrollbar ${
        products && products.length ? 'md:min-h-[400px]' : 'h-[50vh] lg:h-[80vh] justify-center items-center w-full'
      }`}
      style={containerStyles}
    >
      {isLoading ? (
        // TODO: Replace with appropriate loader
        <div className='flex items-center gap-3 pointer-events-auto py-6'>
          <span className='b8--medium text-gray-600 animate-pulse'>Loading products...</span>
          <span className='spinner spinner--md'></span>
        </div>
      ) : products && products.length ? (
        <>
          <div
            id='product-grid'
            className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-2 md:gap-x-3 gap-y-5'
          >
            {paginatedProducts.map((product, index) => (
              <ProductTile
                product={product}
                swatches={product?.swatches}
                siblings={undefined}
                settings={settings}
                globals={undefined}
                type={settings.settings.tile_type}
                enableWishlist={true}
                showPrice={settings.settings.show_price}
                showQuickAdd={settings.settings.show_quickadd}
                showTags={settings.settings.show_tags}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className='flex justify-center mt-8'>
              <ProductListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={isLoading}
                goToPage={(page) => {
                  setCurrentPage(page);
                  setTimeout(() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }, 500);
                }}
                loadMoreProducts={() => {}}
                paginationType='pages'
                paginationTextClass={'b6--bold'}
                paginationActiveColour={'#101010'}
                paginationInactiveColour={'#C2C2C2'}
              />
            </div>
          )}
        </>
      ) : (
        <div className='flex justify-center items-center flex-col w-full'>
          <Paragraph
            type='b7--medium text-center'
            text={locales?.wishlist?.empty || 'Your wishlist is empty.'}
          />
          <Button
            onClick={() => (window.location.href = '/collections/all')}
            className='button button-primary mt-6 px-8'
            label='Continue Shopping'
          />
        </div>
      )}
    </AccountSectionLayout>
  );
};
